import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';

export interface TeacherCardData {
  id: string;
  name: string;
  image: string;
  speciality: string;
  biography?: string;
  /** Extra subjects beyond the primary badge (shows «+N مواد») */
  extraSubjectsCount?: number;
}

interface TeacherCardProps {
  teacher: TeacherCardData;
  onClick: (teacherId: string) => void;
  compact?: boolean;
  /** Base path for teacher profile link — default `/teacher/` */
  teacherPathPrefix?: string;
}

const TeacherCard: React.FC<TeacherCardProps> = ({
  teacher,
  onClick,
  compact = false,
  teacherPathPrefix = '/teacher/',
}) => {
  const titleId = `teacher-card-title-${teacher.id}`;
  const bio = teacher.biography?.trim() || teacher.speciality;
  const prefix = teacherPathPrefix.endsWith('/') ? teacherPathPrefix : `${teacherPathPrefix}/`;
  const href = `${prefix}${teacher.id}`;

  return (
    <article
      role="listitem"
      className={`group flex h-full w-full min-w-0 cursor-pointer flex-col ${cardTheme.courseCard} ${
        compact ? 'rounded-xl' : 'rounded-2xl'
      }`}
      aria-labelledby={titleId}
      onClick={() => onClick(teacher.id)}
    >
      <div
        className={`relative w-full shrink-0 overflow-hidden bg-green-50 ${
          compact ? 'aspect-[4/5]' : 'aspect-[3/4]'
        }`}
      >
        <img
          src={teacher.image}
          alt={`صورة ${teacher.name}`}
          width={compact ? 240 : 360}
          height={compact ? 300 : 480}
          loading="lazy"
          decoding="async"
          sizes={
            compact
              ? '(max-width: 639px) 42vw, (max-width: 1023px) 28vw, 200px'
              : '(max-width: 639px) 90vw, (max-width: 1023px) 45vw, 320px'
          }
          className="absolute inset-0 z-[1] h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = '/default-teacher.jpg';
          }}
        />
        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-green-950/55 via-transparent to-transparent" />
      </div>

      <div
        className={`flex flex-1 flex-col border-t-4 border-t-gold-500 bg-white ${
          compact ? 'p-2.5 sm:p-3' : 'p-4 sm:p-5'
        }`}
      >
        <div className={compact ? 'space-y-1' : 'space-y-2'}>
          <h3
            id={titleId}
            className={`line-clamp-2 font-bold leading-snug ${cardTheme.title} ${
              compact ? 'text-sm sm:text-[0.95rem]' : 'text-base sm:text-lg leading-relaxed'
            }`}
            title={teacher.name}
          >
            {teacher.name}
          </h3>
          <p
            className={`leading-relaxed ${cardTheme.subtitle} ${
              compact ? 'line-clamp-2 text-xs' : 'line-clamp-3 text-sm'
            }`}
          >
            {bio}
          </p>
          {!compact && <div className={`mt-1 h-0.5 w-10 ${cardTheme.divider}`} />}
        </div>

        <div className={compact ? 'mt-auto pt-2.5' : 'mt-auto pt-4'}>
          <Link
            to={href}
            onClick={(e) => e.stopPropagation()}
            aria-label={`شوف كورسات ${teacher.name}`}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl ${cardTheme.btnPrimary} ${
              compact
                ? 'min-h-9 px-2.5 text-xs font-semibold'
                : 'min-h-11 gap-2 px-4 text-sm'
            }`}
          >
            <BookOpen className={compact ? 'h-3.5 w-3.5 shrink-0' : 'h-4 w-4 shrink-0'} aria-hidden />
            شوف الكورسات
          </Link>
        </div>
      </div>
    </article>
  );
};

export default TeacherCard;
